import cn from 'classnames';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { updatePersonalizationSettingsAction as updatePersonalizationSettingsOperation } from 'editor/src/store/design/slice';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import SwitchButton from 'editor/src/component/SwitchButton';

import styles from './index.module.scss';

function PrintFileUploadSetting() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const visible = useSelector((state) => !!getHostSetting(state, 'allowPrintFileUpload'));
  const initialEnabled = useSelector((state) => !!getHostSetting(state, 'printFileUploadEnabled'));
  const on = useSelector((state) => !!state.design.designData?.personalizationSettings?.allowPrintFileProcessing);

  // Seed the toggle from the host-provided initial value once, when the feature is on
  // and the design has no value yet (i.e. opening a saved product).
  useEffect(() => {
    if (visible && initialEnabled && !on) {
      dispatch(updatePersonalizationSettingsOperation({ allowPrintFileProcessing: true }));
    }
  }, [visible, initialEnabled]);

  // Keep the host in sync with the toggle's value on EVERY change — including the on-load
  // restore from saved design data, not just user clicks. Without this, reopening a product
  // shows the toggle on while the host flag stays false (stale), so the empty-design bypass
  // and the metadata write never fire.
  useEffect(() => {
    if (visible) {
      sendPostMessage('design.printFileUploadChanged', { enabled: on });
    }
  }, [on, visible]);

  const onSwitch = useCallback(
    (next: boolean) => {
      dispatch(updatePersonalizationSettingsOperation({ allowPrintFileProcessing: next || undefined }));
    },
    [dispatch],
  );

  if (!visible) {
    return null;
  }

  return (
    <div className={cn(styles.card, { [styles.on]: on })} data-key="print-file-upload-setting">
      <div className={styles.row}>
        <SwitchButton on={on} onSwitch={onSwitch} className={cn(styles.switch, 'cy-allow-print-file-upload')} />
        <div className={styles.title}>{t('Let customers upload their own print file at checkout')}</div>
      </div>
      <div className={styles.description}>
        {t('Buyers upload a print-ready file (PDF, PNG or JPEG) on the product page and Gelato prints it as-is.')}
      </div>
    </div>
  );
}

export default PrintFileUploadSetting;
